/**
 * Custom hook for SupplierDetail state management
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  suppliersAPI,
  SupplierProfile,
  purchasingAPI,
  PurchaseOrder,
  qualityAPI,
  NonConformanceReport,
  coreAPI,
  UserProfile,
  UserSitePermission,
  Company,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useIsCustomer, usePortalMode } from '../../hooks/usePortalMode';
import { OtifSettings, SupplierDetailHook } from './types';
import { getApiErrorMessage } from '../../utils/errorUtils';

export function useSupplierDetail(): SupplierDetailHook {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';

  const { startImpersonation } = useAuth();

  // Supplier data state
  const [supplier, setSupplier] = useState<SupplierProfile | null>(null);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [ncrs, setNCRs] = useState<NonConformanceReport[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ncrsLoading, setNCRsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Active tab (honours ?tab=<name> on initial load)
  const [activeTab, setActiveTab] = useState(initialTab);

  // User modal state
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalTab, setUserModalTab] = useState<'profile' | 'sites'>('profile');
  const [userSitePermissions, setUserSitePermissions] = useState<UserSitePermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [savingPermission, setSavingPermission] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [impersonating, setImpersonating] = useState<number | null>(null);

  // OTIF Settings state
  const [otifSettings, setOtifSettings] = useState<OtifSettings | null>(null);
  const [otifLoading, setOtifLoading] = useState(false);
  const [otifSaving, setOtifSaving] = useState(false);

  // Computed values
  const { isHelix: isHelixAdmin } = usePortalMode();
  const isCustomer = useIsCustomer();
  const { user } = useAuth();
  const isOwnCompany = !!(supplier && user && user.company === supplier.company);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (!userSearch) return true;
      const search = userSearch.toLowerCase();
      return (
        u.user.full_name?.toLowerCase().includes(search) ||
        u.user.email.toLowerCase().includes(search) ||
        u.job_title?.toLowerCase().includes(search) ||
        u.role_name?.toLowerCase().includes(search)
      );
    });
  }, [users, userSearch]);

  // Fetch supplier data on mount
  useEffect(() => {
    if (id) {
      fetchSupplierData();
    }
  }, [id, isCustomer]);

  // Lazy load tab data
  useEffect(() => {
    if (activeTab === 'orders' && orders.length === 0 && supplier) {
      fetchOrders();
    } else if (activeTab === 'quality' && ncrs.length === 0 && supplier) {
      fetchNCRs();
    } else if (activeTab === 'users' && users.length === 0 && supplier) {
      fetchUsers();
    } else if ((activeTab === 'performance' || activeTab === 'settings') && !otifSettings && supplier) {
      fetchOtifSettings();
    }
  }, [activeTab, supplier]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await suppliersAPI.getProfile(Number(id));
      setSupplier(response.data);
    } catch (err: unknown) {
      console.error('Error fetching supplier:', err);
      setError(getApiErrorMessage(err, 'Failed to load supplier details'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    if (!supplier) return;

    try {
      setOrdersLoading(true);
      const response = await purchasingAPI.getOrders({
        supplier: supplier.company,
        ordering: '-created_at',
        page_size: 10,
      });
      setOrders(response.data.results);
    } catch (err: unknown) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchNCRs = async () => {
    if (!supplier) return;

    try {
      setNCRsLoading(true);
      const response = await qualityAPI.getNCRs({
        supplier: supplier.company,
        ordering: '-created_at',
        page_size: 10,
      });
      setNCRs(response.data.results);
    } catch (err: unknown) {
      console.error('Error fetching NCRs:', err);
    } finally {
      setNCRsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!supplier) return;

    try {
      setUsersLoading(true);
      const response = await coreAPI.getCompanyUsers(supplier.company);
      setUsers(response.data);
    } catch (err: unknown) {
      console.error('Error fetching users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchOtifSettings = async () => {
    if (!supplier) return;

    try {
      setOtifLoading(true);
      const response = await suppliersAPI.getOtifSettings(supplier.company);
      setOtifSettings({
        use_custom_otif_window: response.data.use_custom_otif_window,
        otif_days_early_allowed: response.data.otif_days_early_allowed,
        otif_days_late_allowed: response.data.otif_days_late_allowed,
      });
    } catch (err: unknown) {
      console.error('Error fetching OTIF settings:', err);
    } finally {
      setOtifLoading(false);
    }
  };

  const handleOpenUserModal = async (userProfile: UserProfile) => {
    setSelectedUser(userProfile);
    setUserModalTab('profile');
    setUserModalOpen(true);

    try {
      setPermissionsLoading(true);
      // CR-5344: Pass the supplier company id so the popup shows sites
      // scoped to THIS supplier's group (not the user's home group).
      // Multi-company users (Helix admins, COOs etc.) would otherwise
      // leak their other-tenant site memberships into this dialog.
      const response = await coreAPI.getUserSitePermissions(
        userProfile.id,
        supplier?.company ?? undefined
      );
      setUserSitePermissions(response.data);
    } catch (err: unknown) {
      console.error('Error fetching user permissions:', err);
      setUserSitePermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setSelectedUser(null);
    setUserSitePermissions([]);
    setUserModalTab('profile');
  };

  const handleToggleSiteLink = async (siteId: number, isLinked: boolean) => {
    if (!selectedUser) return;

    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        is_linked: isLinked,
        is_active: isLinked,
        can_purchasing: isLinked,
        can_quality: isLinked,
        can_finance: isLinked,
      });
      setUserSitePermissions((prev) =>
        prev.map((p) => (p.site === siteId ? response.data : p))
      );
    } catch (err: unknown) {
      console.error('Error updating site permission:', err);
    } finally {
      setSavingPermission(null);
    }
  };

  const handleUpdatePermission = async (siteId: number, field: string, value: boolean) => {
    if (!selectedUser) return;

    setSavingPermission(siteId);
    try {
      const response = await coreAPI.updateUserSitePermission(selectedUser.id, siteId, {
        [field]: value,
      });
      setUserSitePermissions((prev) =>
        prev.map((p) => (p.site === siteId ? response.data : p))
      );
    } catch (err: unknown) {
      console.error('Error updating permission:', err);
    } finally {
      setSavingPermission(null);
    }
  };

  const handleOtifSettingsUpdate = async (settings: Partial<OtifSettings>) => {
    if (!supplier) return;

    try {
      setOtifSaving(true);
      const response = await suppliersAPI.updateOtifSettings(supplier.company, settings);
      setOtifSettings({
        use_custom_otif_window: response.data.data.use_custom_otif_window,
        otif_days_early_allowed: response.data.data.otif_days_early_allowed,
        otif_days_late_allowed: response.data.data.otif_days_late_allowed,
      });
    } catch (err: unknown) {
      console.error('Error updating OTIF settings:', err);
    } finally {
      setOtifSaving(false);
    }
  };

  const handleUpdateProfile = async (data: Partial<SupplierProfile>) => {
    if (!supplier) return;
    const response = await suppliersAPI.updateProfile(supplier.company, data);
    setSupplier(response.data);
  };

  const handleUpdateCompany = async (data: Record<string, string | number | boolean | null>) => {
    if (!supplier) return;
    // CR-4815: send only the delta. Spreading supplier.company_details included
    // read-only/computed fields that the backend rejected, which caused the
    // Company PATCH to fail AFTER the SupplierProfile PATCH had already saved —
    // producing a partial update + misleading error toast.
    await coreAPI.updateCompany(supplier.company, data as Partial<Company>);
    // Refresh supplier data to get updated company_details
    await fetchSupplierData();
  };

  const handleUploadLogo = async (file: File) => {
    if (!supplier) return;
    await coreAPI.uploadCompanyLogo(supplier.company, file);
    await fetchSupplierData();
  };

  const handleImpersonate = async (userProfile: UserProfile) => {
    if (!isHelixAdmin && !isCustomer) return;

    try {
      setImpersonating(userProfile.user.id);
      const { authAPI } = await import('../../lib/api');
      // CR-4858 follow-up: pass view_company_id to the backend so it can
      // pin profile.current_company / current_site to the same company
      // the admin clicked through. Without this the backend would default
      // to the target user's home — which for multi-company users is
      // rarely what the admin intended (see CR-4858 description).
      const response = await authAPI.impersonate(
        userProfile.user.id,
        undefined,
        supplier?.company,
      );

      // API returns standardized format: { status, message, data: { target_user, target_token, original_token } }
      const impersonationData = response.data.data;

      // CR-4858: Pin the post-impersonation landing scope to *this* supplier
      // (the company the admin clicked through), not the impersonated user's
      // primary company. Multi-company users would otherwise land on whichever
      // company is stamped as their primary, which is rarely what the admin
      // intended when triaging from a specific supplier's Users tab.
      const contextCompany = supplier
        ? { id: supplier.company, name: supplier.company_name }
        : undefined;

      // Start impersonation (triggers full page reload)
      startImpersonation(
        impersonationData.target_user,
        impersonationData.target_token,
        impersonationData.original_token,
        contextCompany
      );
    } catch (err: unknown) {
      console.error('Error impersonating user:', err);
    } finally {
      setImpersonating(null);
    }
  };

  return {
    // State
    supplier,
    orders,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    error,
    activeTab,
    selectedUser,
    userModalOpen,
    userModalTab,
    userSitePermissions,
    permissionsLoading,
    savingPermission,
    userSearch,
    impersonating,
    otifSettings,
    otifLoading,
    otifSaving,

    // Actions
    fetchSupplierData,
    fetchOrders,
    fetchNCRs,
    fetchUsers,
    fetchOtifSettings,
    setActiveTab,
    handleOpenUserModal,
    handleCloseUserModal,
    handleToggleSiteLink,
    handleUpdatePermission,
    setUserSearch,
    handleOtifSettingsUpdate,
    setOtifSettings,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    handleImpersonate,
    setUserModalTab,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isCustomer,
    isOwnCompany,
  };
}
